import type { Api } from './api';
import type { Folder, Summary } from './views/MailPage';

/** Messaggio trovato, con la cartella in cui sta (posta in arrivo o inviata). */
export interface FoundMail extends Summary {
  folder: string;
  folderKind: 'inbox' | 'sent';
}

let foldersCache: Promise<Folder[]> | null = null;

/** Cerca nella posta in arrivo e in quella inviata (le ultime 30 di ciascuna), dalla più recente. */
export async function searchMail(api: Api, term: string): Promise<FoundMail[]> {
  const t = term.trim();
  if (t.length < 3) return [];
  foldersCache ??= api.get<Folder[]>('/api/mail/folders').catch((e) => {
    foldersCache = null;
    throw e;
  });
  const folders = await foldersCache;
  const inbox = folders.find((f) => f.kind === 'inbox')?.path ?? 'INBOX';
  const sent = folders.find((f) => f.kind === 'sent')?.path;
  const one = async (folder: string, kind: 'inbox' | 'sent') => {
    const r = await api.get<{ total: number; messages: Summary[] }>(`/api/mail/messages?folder=${encodeURIComponent(folder)}&search=${encodeURIComponent(t)}`);
    return r.messages.map((m) => ({ ...m, folder, folderKind: kind }));
  };
  const lists = await Promise.all([one(inbox, 'inbox'), sent ? one(sent, 'sent').catch(() => []) : Promise.resolve([])]);
  return lists.flat().sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
}

const GENERIC = /(^|\.)(gmail|googlemail|libero|hotmail|outlook|live|yahoo|icloud|me|virgilio|alice|tin|tiscali|fastwebnet|aruba|pec|legalmail|postecert|email|msn)\./i;

/** Cosa cercare per un'azienda: il dominio aziendale delle email (es. carvespackaging.it), altrimenti l'indirizzo, altrimenti il nome. */
export function mailTermFor(emails: (string | null | undefined)[], name: string): string {
  const list = emails.filter((e): e is string => !!e && e.includes('@'));
  for (const e of list) {
    const domain = e.split('@')[1].toLowerCase();
    if (!GENERIC.test(domain + '.') && !/pec\./.test(domain)) return domain;
  }
  if (list[0]) return list[0];
  return name
    .replace(/\(.*?\)/g, ' ')
    .replace(/\b(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?a\.?s\.?|s\.?n\.?c\.?)\b/gi, ' ')
    // il punto finale di «S.r.l.» restava da solo: «Caseificio Fratelli Beneduce .»
    .replace(/(^|\s)[.,;:]+(?=\s|$)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
